import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { signUp, confirmSignUp, resendSignUpCode, signIn } from "aws-amplify/auth";
import { useAuth } from "@/app/context/AuthContext";
import {
  AuthCard,
  AuthPasswordField,
  TermsAgreementDialog,
  authCheckboxSx,
  authFeedbackSx,
  authFieldSx,
  authFooterSx,
  authFormControlLabelSx,
  authLinkSx,
  authMutedTextSx,
  authPrimaryActionSx,
} from "./components";
import { authErrorCopy, isPrivateConfirmationFailure, PASSWORD_PATTERN, PASSWORD_RULE } from "./auth-copy";
import { completeConfirmedSignup } from "./signup-flow";
import { trackProductEvent } from "@/shared/analytics/ga4";
import { TERMS_ACCEPTANCE_STORAGE_KEY, TERMS_VERSION } from "@/shared/constants/legal";
import { SIGNUP_EMAIL_PREFERENCES_KEY } from "@/features/profile/email-preferences.api";
import { buildSignUpInput, validateSignupDetails } from "./signup-details";

const LEGAL_SITE_URL = import.meta.env.VITE_MARKETING_URL ?? "https://chessla.net";

export const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [step, setStep] = useState<"signup" | "confirm" | "verified">(
    searchParams.get("confirm") === "1" ? "confirm" : "signup",
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [newsletter, setNewsletter] = useState(false);

  const continueToProfile = async () => {
    setPassword("");
    await refetch();
    trackProductEvent("signup_completed");
    void navigate("/profile");
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const detailsError = validateSignupDetails({ firstName, lastName, birthdate });
    if (detailsError) {
      setError(detailsError);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await signUp(buildSignUpInput({ firstName, lastName, birthdate, email, password }));
      localStorage.setItem(TERMS_ACCEPTANCE_STORAGE_KEY, TERMS_VERSION);
      localStorage.setItem(SIGNUP_EMAIL_PREFERENCES_KEY, JSON.stringify({ newsletterEnabled: newsletter }));

      if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        // Cognito sent a verification code — show the confirmation form
        setStep("confirm");
      } else if (result.nextStep.signUpStep === "DONE") {
        // Auto-confirmed — continue setup on the profile page.
        const login = await signIn({ username: email, password });
        if (login.isSignedIn) await continueToProfile();
        else setStep("verified");
      }
    } catch (err: unknown) {
      setError(authErrorCopy(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await completeConfirmedSignup(
        { confirm: confirmSignUp, signIn },
        { email, password, confirmationCode },
      );
      if (result === "signed_in") await continueToProfile();
      else {
        setPassword("");
        setStep("verified");
      }
    } catch (err: unknown) {
      if (isPrivateConfirmationFailure(err))
        setResendMessage("If this account is waiting for confirmation, a new code is on its way.");
      else setError(authErrorCopy(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setResendMessage(null);
    setIsSubmitting(true);
    try {
      await resendSignUpCode({ username: email });
      setResendMessage("A new code is on its way.");
      setResendCooldown(60);
      // Countdown timer
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: unknown) {
      setError(authErrorCopy(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "verified") {
    return (
      <AuthCard title="EMAIL VERIFIED" detail="Your account is ready. Log in to continue to your private workspace.">
        <Button component={Link} to="/login" variant="contained" fullWidth sx={authPrimaryActionSx}>
          CONTINUE TO LOG IN
        </Button>
      </AuthCard>
    );
  }

  if (step === "confirm") {
    return (
      <AuthCard
        title="CHECK YOUR EMAIL"
        detail={
          email ? (
            <>Enter the verification code sent to {email}</>
          ) : (
            "Enter the email and verification code from your inbox."
          )
        }
      >
        <form onSubmit={(event) => void handleConfirm(event)}>
          <TextField
            autoComplete="email"
            autoCapitalize="none"
            label="Email"
            name="email"
            spellCheck={false}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
            sx={authFieldSx}
          />
          <TextField
            autoComplete="one-time-code"
            label="Verification code"
            name="confirmation-code"
            type="text"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            required
            fullWidth
            sx={authFieldSx}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          {error && (
            <SnackbarNotice severity="error" sx={authFeedbackSx}>
              {error}
            </SnackbarNotice>
          )}
          {resendMessage && (
            <SnackbarNotice severity="success" sx={authFeedbackSx}>
              {resendMessage}
            </SnackbarNotice>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={authPrimaryActionSx}>
            {isSubmitting ? "VERIFYING…" : "VERIFY EMAIL"}
          </Button>
          <Typography variant="caption" sx={{ ...authMutedTextSx, display: "block", mt: 2 }}>
            Didn&apos;t receive a code?{" "}
            <Button
              type="button"
              onClick={() => void handleResendCode()}
              disabled={resendCooldown > 0 || isSubmitting}
              sx={{
                ...authLinkSx,
                fontWeight: 900,
                minHeight: 44,
                textTransform: "none",
              }}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </Button>
          </Typography>
          <Button
            type="button"
            onClick={() => {
              setConfirmationCode("");
              setError(null);
              setStep("signup");
            }}
            sx={{ ...authMutedTextSx, minHeight: 44, borderRadius: 0 }}
          >
            CHANGE EMAIL
          </Button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="CREATE YOUR ACCOUNT">
      <form onSubmit={(event) => void handleSignUp(event)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { sm: 1.5 } }}>
          <TextField
            autoComplete="given-name"
            label="First name"
            name="firstName"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            fullWidth
            sx={authFieldSx}
          />
          <TextField
            autoComplete="family-name"
            label="Last name"
            name="lastName"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            fullWidth
            sx={authFieldSx}
          />
        </Box>
        <TextField
          autoComplete="bday"
          helperText="Private. Used only to confirm age eligibility."
          InputLabelProps={{ shrink: true }}
          label="Date of birth"
          name="birthdate"
          type="date"
          value={birthdate}
          onChange={(event) => setBirthdate(event.target.value)}
          required
          fullWidth
          sx={authFieldSx}
        />
        <TextField
          autoComplete="email"
          autoCapitalize="none"
          label="Email"
          name="email"
          spellCheck={false}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          sx={authFieldSx}
        />
        <AuthPasswordField
          autoComplete="new-password"
          label="Password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          inputProps={{ minLength: 8, pattern: PASSWORD_PATTERN, title: PASSWORD_RULE }}
          helperText={PASSWORD_RULE}
          required
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedAgreement}
              onChange={(event) => setAcceptedAgreement(event.target.checked)}
              required
              size="small"
              sx={authCheckboxSx}
            />
          }
          sx={authFormControlLabelSx}
          label={
            <span>
              I agree to the{" "}
              <Button
                type="button"
                onClick={() => setTermsOpen(true)}
                sx={{
                  ...authLinkSx,
                  font: "inherit",
                  minHeight: 44,
                  minWidth: 0,
                  p: 0,
                  textTransform: "none",
                }}
              >
                Terms
              </Button>{" "}
              and acknowledge the{" "}
              <a href={`${LEGAL_SITE_URL}/privacy`} target="_blank" rel="noreferrer" style={authLinkSx}>
                Privacy Notice
              </a>
              .
            </span>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={newsletter}
              onChange={(event) => setNewsletter(event.target.checked)}
              size="small"
              sx={authCheckboxSx}
            />
          }
          label="Send me the optional Chessla newsletter"
          sx={authFormControlLabelSx}
        />
        {error && (
          <SnackbarNotice severity="error" sx={authFeedbackSx}>
            {error}
          </SnackbarNotice>
        )}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting || !acceptedAgreement}
          sx={authPrimaryActionSx}
        >
          {isSubmitting ? "CREATING ACCOUNT…" : "CREATE ACCOUNT"}
        </Button>
      </form>
      <Typography variant="caption" sx={authFooterSx}>
        Already have an account?{" "}
        <Link to="/login" style={authLinkSx}>
          LOG IN
        </Link>
      </Typography>
      <TermsAgreementDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
    </AuthCard>
  );
};
